import React, { RefObject, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button } from "ui-kit/Button";
import { FormattedMessage } from "react-intl";
import { LeftPanelEasy } from "./Easy";
import { LeftPanelCustom } from "./Custom";
import TrashIcon from "assets/trash.svg";
import { Track } from "../data/schema";
import { DeviceSize } from "constants/deviceSize";

export type LeftPanelMode = "easy" | "custom";

type LeftPanelProps = {
  mode: LeftPanelMode;
  createEnabled?: boolean;
  onCreate?: () => void | Promise<void>;
  isCreating?: boolean;
  // Easy
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  songDescription: string;
  setSongDescription: (v: string) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isLyrics: boolean;
  setIsLyrics: (v: boolean) => void;
  isInstrumental: boolean;
  setIsInstrumental: (v: boolean) => void;
  availableInspirations: string[];
  onInspirationClick: (item: string) => void;
  inspirationCount?: number;
  inspirationTotal?: number;
  onGeneratePrompt?: () => void;
  isGeneratingPrompt?: boolean;
  onEasyLyricsClick?: () => void;
  onInspirationScrollEnd?: () => void;
  easyPromptMaxLength: number;
  // Custom
  openSections: { lyrics: boolean; styles: boolean; advanced: boolean };
  toggleSection: (section: 'lyrics' | 'styles' | 'advanced') => void;
  isLyricsExpanded: boolean;
  onToggleLyricsExpand: () => void;
  stylesDescription: string;
  setStylesDescription: (v: string) => void;
  availableStylesInspirations: string[];
  onStylesInspirationClick: (v: string) => void;
  vocalGender: 'male' | 'female' | '';
  setVocalGender: (v: 'male' | 'female' | '') => void;
  weirdness: number;
  styleInfluence: number;
  audioWeight: number;
  onSliderMouseDown: (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => void;
  onTrackClick: (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => void;
  showTooltip: { vocalGender: boolean; weirdness: boolean; styleInfluence: boolean; audioWeight: boolean };
  setShowTooltip: React.Dispatch<React.SetStateAction<{ vocalGender: boolean; weirdness: boolean; styleInfluence: boolean; audioWeight: boolean }>>;
  songTitle: string;
  setSongTitle: (v: string) => void;
  lyricsText: string;
  setLyricsText: (v: string) => void;
  saveWorkspaceId: string | null;
  setSaveWorkspaceId: (id: string) => void;
  className?: string;
  hasPlayer?: boolean;
  stylesDisplayedCount?: number;
  stylesTotal?: number;
  lyricsTotal?: number;
  onGenerateLyricsPrompt?: () => void;
  isGeneratingLyricsPrompt?: boolean;
  onGenerateStyles?: () => void;
  isGeneratingStyles?: boolean;
  lyricsPromptMenu?: React.ReactNode;
  lyricsPromptMenuRef?: React.RefObject<HTMLDivElement | null>;
  onStylesScrollEnd?: () => void;
  showLyricsPromptButton?: boolean;
  customStyleMaxLength: number;
  customTitleMaxLength: number;
  customLyricsMaxLength: number;
  hasPersonas?: boolean;
  onPersonaClick?: () => void;
  selectedPersonaName?: string | null;
  selectedPersonaImageUrl?: string | null;
  onPersonaClear?: () => void;
  onResetCustomAdvanced?: () => void;
  onPlayReferenceAudio?: (track: Track) => void;
  playingTrack?: Track | null;
  isPlaying?: boolean;
  onGlobalPlayerToggle?: () => void;
};

export const LeftPanel: React.FC<LeftPanelProps> = (props) => {
  const { mode, className } = props;

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  // Когда переключаемся в Easy — всегда скроллим контейнер в самый верх
  useEffect(() => {
    if (mode !== "easy") return;
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [mode]);

  const hasTrashButton =
    (mode === "easy" && props.songDescription.trim().length > 0) ||
    (mode === "custom" &&
      (props.lyricsText.trim().length > 0 || props.stylesDescription.trim().length > 0));

  const handleClearClick = () => {
    if (mode === "easy") {
      props.setSongDescription("");
    } else {
      props.setLyricsText("");
      props.setStylesDescription("");
      props.setSongTitle("");
      props.onResetCustomAdvanced?.();
    }
  };
  return (
    <Container className={className}>
      <HeaderDivider />
      <ScrollArea ref={scrollAreaRef} $hasPlayer={props.hasPlayer} $mode={mode}>
        <PanelsWrapper>
          <SlidingPanel $mode={mode} $panel="easy">
            <LeftPanelEasy
              textareaRef={props.textareaRef}
              songDescription={props.songDescription}
              onDescriptionChange={props.onDescriptionChange}
              isLyrics={props.isLyrics}
              setIsLyrics={props.setIsLyrics}
              isInstrumental={props.isInstrumental}
              setIsInstrumental={props.setIsInstrumental}
              availableInspirations={props.availableInspirations}
              onInspirationClick={props.onInspirationClick}
              promptMaxLength={props.easyPromptMaxLength}
              inspirationCount={props.inspirationCount}
              inspirationTotal={props.inspirationTotal}
              onGeneratePrompt={props.onGeneratePrompt}
              isGeneratingPrompt={props.isGeneratingPrompt}
              onLyricsClick={props.onEasyLyricsClick}
              onInspirationScrollEnd={props.onInspirationScrollEnd}
            />
          </SlidingPanel>
          <SlidingPanel $mode={mode} $panel="custom">
            <LeftPanelCustom
              openSections={props.openSections}
              toggleSection={props.toggleSection}
              isLyricsExpanded={props.isLyricsExpanded}
              onToggleLyricsExpand={props.onToggleLyricsExpand}
              stylesDescription={props.stylesDescription}
              setStylesDescription={props.setStylesDescription}
              availableStylesInspirations={props.availableStylesInspirations}
              onStylesInspirationClick={props.onStylesInspirationClick}
              vocalGender={props.vocalGender}
              setVocalGender={props.setVocalGender}
              weirdness={props.weirdness}
              styleInfluence={props.styleInfluence}
              audioWeight={props.audioWeight}
              onSliderMouseDown={props.onSliderMouseDown}
              onTrackClick={props.onTrackClick}
              showTooltip={props.showTooltip}
              setShowTooltip={props.setShowTooltip}
              songTitle={props.songTitle}
              setSongTitle={props.setSongTitle}
              lyricsText={props.lyricsText}
              setLyricsText={props.setLyricsText}
              saveWorkspaceId={props.saveWorkspaceId}
              setSaveWorkspaceId={props.setSaveWorkspaceId}
              hasPlayer={props.hasPlayer}
              stylesDisplayedCount={props.stylesDisplayedCount}
              stylesTotal={props.stylesTotal}
              lyricsTotal={props.lyricsTotal}
              onGenerateLyricsPrompt={props.onGenerateLyricsPrompt}
              isGeneratingLyricsPrompt={props.isGeneratingLyricsPrompt}
              onGenerateStyles={props.onGenerateStyles}
              isGeneratingStyles={props.isGeneratingStyles}
              lyricsPromptMenu={props.lyricsPromptMenu}
              lyricsPromptMenuRef={props.lyricsPromptMenuRef}
              onStylesScrollEnd={props.onStylesScrollEnd}
              showLyricsPromptButton={props.showLyricsPromptButton}
              styleMaxLength={props.customStyleMaxLength}
              titleMaxLength={props.customTitleMaxLength}
              lyricsMaxLength={props.customLyricsMaxLength}
              hasPersonas={props.hasPersonas}
              onPersonaClick={props.onPersonaClick}
              selectedPersonaName={props.selectedPersonaName}
              selectedPersonaImageUrl={props.selectedPersonaImageUrl}
              onPersonaClear={props.onPersonaClear}
              onPlayReferenceAudio={props.onPlayReferenceAudio}
              playingTrack={props.playingTrack}
              isPlaying={props.isPlaying}
              onGlobalPlayerToggle={props.onGlobalPlayerToggle}
            />
          </SlidingPanel>
        </PanelsWrapper>
      </ScrollArea>
      <FooterOverlay $hasPlayer={props.hasPlayer}>
        <ButtonsRow $hasTrash={hasTrashButton}>
          <TrashButton
            type="button"
            $visible={hasTrashButton}
            onClick={hasTrashButton ? handleClearClick : undefined}
            aria-label="Clear prompt"
          >
            <TrashIcon />
          </TrashButton>
          <CreateButtonWrapper $compressed={hasTrashButton}>
            <CreateInterfaceButton
              disabled={!props.createEnabled || props.isCreating}
              $disabled={!props.createEnabled || props.isCreating}
              onClick={props.createEnabled && !props.isCreating ? props.onCreate : undefined}
            >
              {props.isCreating ? 'Generating...' : (
                <FormattedMessage
                  id="AICompose.createButton"
                  defaultMessage="Create"
                  description="Create button"
                />
              )}
            </CreateInterfaceButton>
          </CreateButtonWrapper>
        </ButtonsRow>
      </FooterOverlay>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%; /* использовать viewport-единицы для стабильной высоты */
`;

const ScrollArea = styled.div<{ $hasPlayer?: boolean; $mode: LeftPanelMode }>`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $mode }) => ($mode === "easy" ? "hidden" : "auto")};
  overflow-x: hidden;
  padding-bottom: ${p => p.$hasPlayer ? '33vh' : '20vh'}; /* запас под нижнюю кнопку, чтобы можно было долистать контент до конца */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar { /* Chrome, Safari, Opera */
    width: 0px;
    height: 0px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    /* Скролл только внутри контента, без перетаскивания всей модалки */
    touch-action: pan-y;
    overscroll-behavior: contain;
  }
`;

const HeaderDivider = styled.div`
  height: 1px;
  background-color: #e9ecef;
  width: calc(100% + 40px);
  transform: translateX(-20px);

  /* Показываем разделитель только на десктопе */
  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;
const PanelsWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  width: 100%;
  overflow: hidden;
`;

const SlidingPanel = styled.div<{ $mode: LeftPanelMode; $panel: LeftPanelMode }>`
  position: relative;
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  width: 100%;
  transition: transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.4s ease;
  will-change: transform, opacity;
  transform: ${({ $mode, $panel }) => {
    if ($panel === "easy") {
      return $mode === "easy" ? "translateX(0%)" : "translateX(-100%)";
    }
    // panel === "custom"
    return $mode === "easy" ? "translateX(100%)" : "translateX(0%)";
  }};
  opacity: ${({ $mode, $panel }) => ($mode === $panel ? 1 : 0)};
  pointer-events: ${({ $mode, $panel }) => ($mode === $panel ? "auto" : "none")};
`;

export default LeftPanel;


const FooterOverlay = styled.div<{ $hasPlayer?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 20px 2vh 20px;
  z-index: 5; /* перекрывает контент панели */
  width: 100%;
  transition: bottom 0.3s ease;
  @media (max-width: ${DeviceSize.TabletMedium}) {
    position: sticky;
  }
`;

const ButtonsRow = styled.div<{ $hasTrash?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  gap: ${p => p.$hasTrash ? '12px' : '0'};
`;

const CreateButtonWrapper = styled.div<{ $compressed?: boolean }>`
  flex: 0 0 ${p => p.$compressed ? 'calc(100% - 68px)' : '100%'};
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  transition: flex-basis 0.3s ease;
`;

const CreateInterfaceButton = styled(Button)<{ $disabled?: boolean; $compressed?: boolean; $hasTrashed?: boolean }>`
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  border: none;
  border-radius: 12px;
  padding: 3.2vh 2vw;
  font-size: 20px;
  font-weight: 600;
  color: white;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  outline: none;
  box-shadow: none;
  width: 100%;
  opacity: ${p => p.$disabled ? 0.5 : 1};
  &:hover {
    transform: ${p => p.$disabled ? 'none' : 'translateY(-1px)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
  
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const TrashButton = styled.button<{ $visible?: boolean }>`
  flex: 0 0 ${p => p.$visible ? '56px' : '0'};
  width: ${p => p.$visible ? '56px' : '0'};
  border-radius: 12px;
  border: none;
  background: #f3f4f6;
  color: #4b5563;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${p => p.$visible ? 1 : 0};
  transform: translateX(${p => p.$visible ? '0' : '-12px'});
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};
  transition: opacity 0.3s ease, transform 0.3s ease, background 0.4s ease, color 0.4s ease;
  &:hover {
    background: #e5e7eb;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;


